// services/botAsistente.api.ts — cliente del panel «Asistente de WhatsApp» (round 2026-10-30, etapa B3).
//
// Rutas que usa (todas con la sesión de admin o superadmin; el servidor es quien manda en los permisos):
//   · GET/PUT /config-options/general-settings   → el ajuste `botModoReal` (solo el superadmin lo cambia).
//   · GET/PUT /config-options/twilio-settings    → modelos, límites y los dos modos de prueba.
//   · GET /bot/estado · GET/DELETE /bot/simulaciones · GET /bot/eventos.
//   · POST /bot/probar-asistente → «Probar el asistente» de Modelos y límites (round 2026-10-31): prueba los modelos ESCRITOS en el
//     formulario, sin guardar nada. Sus fallos no son toasts: la pantalla los explica (`LlamadaPruebaAsistente`).
//
// Convenciones (las mismas de notificaciones.api.ts):
//   · Las LECTURAS van `silent`: cada sección muestra su propio error con «Reintentar» y, si además saltara un toast por
//     cada una, una caída del servidor llenaría la pantalla de avisos. Ante un error devuelven `null` (nunca una lista
//     vacía ni un 0 inventado): «no se pudo consultar» y «no hay nada» son cosas distintas.
//   · Las ESCRITURAS no son silenciosas: el motivo que da el servidor («Solo un superadministrador puede…») llega al
//     usuario como toast gracias a apiFetch.
//
// NUNCA hay claves de API ni tokens en estas rutas: la clave de OpenAI vive solo en las variables de entorno de Railway
// y la pantalla no la pide ni la muestra.
import { apiFetch } from "./apiClient";
import type {
  BotEstado,
  BotEstadoResponse,
  BotEvento,
  BotEventoDetalle,
  BotEventosFiltro,
  BotEventosPagina,
  BotSimulacion,
  BotSimulacionesFiltro,
  BotSimulacionesPagina,
  BotVaciarSimulacionesResultado,
  LlamadaPruebaAsistente,
  NombreTareaIA,
  PruebaAsistenteFallo,
  PruebaAsistenteResultado,
  PruebaAsistenteTarea,
  PruebaAsistenteTareas,
  ResultadoPruebaTarea,
  TareaIA,
  TwilioAjustes,
  TwilioSettingsCambio,
  TwilioSettingsLectura,
} from "../interfaces/botAsistente.interfaces";

/** Frase que el superadministrador debe escribir para ENCENDER el modo real (el servidor la exige igual). */
export const FRASE_MODO_REAL = "ACTIVAR MODO REAL";
/** Frase para vaciar las simulaciones (el servidor la exige igual). */
export const FRASE_VACIAR_SIMULACIONES = "VACIAR SIMULACIONES";

export const TAREAS_IA: readonly NombreTareaIA[] = ["gasto.parse", "gasto.correct"];

/**
 * Valores de fábrica del asistente. Son los de twilio/src/libs/configIA.js y limiteTelefono.js; el servidor manda los
 * suyos en `defaults` y esto solo cubre lo que no mande.
 */
export const TWILIO_DEFAULTS: TwilioAjustes = {
  tareas: {
    "gasto.parse": { model: "gpt-4.1-mini", temperature: 0 },
    "gasto.correct": { model: "gpt-4.1-mini", temperature: 0 },
  },
  timeoutMs: 25_000,
  maxTokensSalida: null,
  limiteMensajesPorMinuto: 20,
  guardarTextoEventos: false,
};

const esObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const esNumero = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

const esTextoIso = (v: unknown): v is string => typeof v === "string" && v !== "";

function tareaDe(crudo: unknown, base: TareaIA): TareaIA {
  if (!esObjeto(crudo)) return { ...base };
  return {
    model: typeof crudo.model === "string" && crudo.model !== "" ? crudo.model : base.model,
    // null es un valor válido («no mandarla»); solo un número lo reemplaza.
    temperature: crudo.temperature === null ? null : esNumero(crudo.temperature) ? crudo.temperature : base.temperature,
  };
}

/** Completa lo que el servidor no mande con `base`. Una clave ausente nunca se convierte en un valor inventado a guardar. */
function ajustesDe(crudo: unknown, base: TwilioAjustes): TwilioAjustes {
  const c = esObjeto(crudo) ? crudo : {};
  const tareas = esObjeto(c.tareas) ? c.tareas : {};
  return {
    tareas: {
      "gasto.parse": tareaDe(tareas["gasto.parse"], base.tareas["gasto.parse"]),
      "gasto.correct": tareaDe(tareas["gasto.correct"], base.tareas["gasto.correct"]),
    },
    timeoutMs: esNumero(c.timeoutMs) ? c.timeoutMs : base.timeoutMs,
    maxTokensSalida: c.maxTokensSalida === null ? null : esNumero(c.maxTokensSalida) ? c.maxTokensSalida : base.maxTokensSalida,
    limiteMensajesPorMinuto: esNumero(c.limiteMensajesPorMinuto) ? c.limiteMensajesPorMinuto : base.limiteMensajesPorMinuto,
    guardarTextoEventos: typeof c.guardarTextoEventos === "boolean" ? c.guardarTextoEventos : base.guardarTextoEventos,
  };
}

/**
 * Lee la respuesta de GET /config-options/twilio-settings: `{ data, defaults }` (el mismo envoltorio de los demás
 * ajustes) o, por tolerancia, los valores sueltos. Devuelve `null` si no es un objeto (no hay nada que mostrar).
 */
export function leerTwilioSettings(
  respuesta: unknown,
): { actual: TwilioSettingsLectura; defaults: TwilioAjustes } | null {
  if (!esObjeto(respuesta)) return null;
  const crudo = esObjeto(respuesta.data) ? respuesta.data : respuesta;
  const defaults = ajustesDe(respuesta.defaults, TWILIO_DEFAULTS);
  const ajustes = ajustesDe(crudo, defaults);
  const simuladorHasta = esTextoIso(crudo.simuladorActivoHasta) ? crudo.simuladorActivoHasta : null;
  const diagnosticoHasta = esTextoIso(crudo.diagnosticoActivoHasta) ? crudo.diagnosticoActivoHasta : null;
  return {
    defaults,
    actual: {
      ...ajustes,
      // «Activo» exige la bandera Y un vencimiento: sin hora de vencimiento no hay cuenta regresiva que mostrar y una
      // bandera suelta no puede dejar un modo encendido «para siempre» en pantalla.
      simuladorActivo: crudo.simuladorActivo === true && simuladorHasta !== null,
      simuladorActivoHasta: simuladorHasta,
      diagnosticoActivo: crudo.diagnosticoActivo === true && diagnosticoHasta !== null,
      diagnosticoActivoHasta: diagnosticoHasta,
    },
  };
}

/** Arma la query string sin los valores vacíos. */
function consulta(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const texto = qs.toString();
  return texto ? `?${texto}` : "";
}

const textoONulo = (v: unknown): string | null => (typeof v === "string" ? v : null);
const numeroONulo = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  // La base entrega los BIGINT como texto; un número escrito como texto se acepta, cualquier otra cosa no.
  if (typeof v === "string" && /^-?\d{1,15}$/.test(v)) return Number(v);
  return null;
};

const MAX_MODELOS_EN_DETALLE = 5;
const MAX_LARGO_TEXTO_DETALLE = 80;
// Los mismos topes con que el servidor deja pasar el resumen de mensajes y la lista de cuentas en conflicto (botEventos.js).
const MAX_CANTIDAD_AGREGADA = 100_000;
const MAX_VENTANA_AGREGADA_SEG = 86_400;
const MAX_USUARIOS_EN_CONFLICTO = 20;
const MAX_ID_USUARIO_EN_DETALLE = 99_999_999;

const esEnteroEnRango = (v: unknown, min: number, max: number): v is number => typeof v === "number" && Number.isInteger(v) && v >= min && v <= max;

/**
 * El diagnóstico del turno que trae un evento: SOLO las nueve claves que el servidor deja pasar (`sanearDetalleEvento`,
 * backend/src/services/botEventos.js), cada una con su tipo. Lo que no es de esas claves o no tiene el tipo esperado se descarta
 * (el servidor ya lo hace; aquí se repite para que la pantalla jamás muestre un valor que no sabe leer). `null` si no queda nada.
 */
export function detalleDe(crudo: unknown): BotEventoDetalle | null {
  if (!esObjeto(crudo)) return null;
  const d: BotEventoDetalle = {};
  if (typeof crudo.messageSid === "string" && crudo.messageSid !== "" && crudo.messageSid.length <= MAX_LARGO_TEXTO_DETALLE) d.messageSid = crudo.messageSid;
  if (typeof crudo.fotos === "number" && Number.isInteger(crudo.fotos) && crudo.fotos >= 0) d.fotos = crudo.fotos;
  if (Array.isArray(crudo.modelos)) {
    const modelos = crudo.modelos
      .filter((m): m is string => typeof m === "string" && m !== "" && m.length <= MAX_LARGO_TEXTO_DETALLE)
      .slice(0, MAX_MODELOS_EN_DETALLE);
    if (modelos.length > 0) d.modelos = modelos;
  }
  if (typeof crudo.guardadoEnEsteTurno === "boolean") d.guardadoEnEsteTurno = crudo.guardadoEnEsteTurno;
  if (typeof crudo.ajustesDelPanel === "boolean") d.ajustesDelPanel = crudo.ajustesDelPanel;
  // El resumen de mensajes de extraños («N mensajes en M minutos»): solo booleano y enteros acotados, sin textos.
  if (typeof crudo.agregado === "boolean") d.agregado = crudo.agregado;
  if (esEnteroEnRango(crudo.cantidad, 1, MAX_CANTIDAD_AGREGADA)) d.cantidad = crudo.cantidad;
  if (esEnteroEnRango(crudo.ventanaSeg, 0, MAX_VENTANA_AGREGADA_SEG)) d.ventanaSeg = crudo.ventanaSeg;
  // Las cuentas que comparten el teléfono del remitente: solo ids (enteros), hasta 20.
  if (Array.isArray(crudo.usuariosEnConflicto)) {
    const ids = crudo.usuariosEnConflicto.filter((x): x is number => esEnteroEnRango(x, 1, MAX_ID_USUARIO_EN_DETALLE)).slice(0, MAX_USUARIOS_EN_CONFLICTO);
    if (ids.length > 0) d.usuariosEnConflicto = ids;
  }
  return Object.keys(d).length > 0 ? d : null;
}

/**
 * Una fila de /bot/eventos con la forma que usa la pantalla (`BotEvento`, claves con guion bajo).
 *
 * El backend REAL entrega cada evento en camelCase (`creadoEn`, `tipoMensaje`, `subEstado`, `telefonoMascara`, `usuarioId`,
 * `duracionMs`, `llamadasIa`, `tokensEntrada`, `tokensSalida`, `operacionId`; backend/src/services/botEventos.js
 * `eventoResumido`), mientras que el contrato original del encargo hablaba de snake_case. Con las claves equivocadas la
 * tabla mostraba «—» en la fecha y en todos los campos sin ningún error. Se aceptan LAS DOS formas: la pantalla no depende
 * de cuál sea la que quede. Lo que no es del tipo esperado se vuelve null (nunca se inventa un valor).
 */
export function normalizarEvento(crudo: Record<string, unknown>): BotEvento {
  const dos = (snake: string, camel: string): unknown => (crudo[snake] !== undefined ? crudo[snake] : crudo[camel]);
  const evento: BotEvento = {
    id: numeroONulo(crudo.id) as number,
    creado_en: textoONulo(dos("creado_en", "creadoEn")) ?? "",
    origen: textoONulo(crudo.origen) ?? "",
    tipo_mensaje: textoONulo(dos("tipo_mensaje", "tipoMensaje")),
    menu: textoONulo(crudo.menu),
    sub_estado: textoONulo(dos("sub_estado", "subEstado")),
    resultado: textoONulo(crudo.resultado) ?? "",
    codigo: textoONulo(crudo.codigo),
    telefono_mascara: textoONulo(dos("telefono_mascara", "telefonoMascara")),
    usuario_id: numeroONulo(dos("usuario_id", "usuarioId")),
    rol: textoONulo(crudo.rol),
    duracion_ms: numeroONulo(dos("duracion_ms", "duracionMs")),
    llamadas_ia: numeroONulo(dos("llamadas_ia", "llamadasIa")),
    tokens_entrada: numeroONulo(dos("tokens_entrada", "tokensEntrada")),
    tokens_salida: numeroONulo(dos("tokens_salida", "tokensSalida")),
    simulado: crudo.simulado === true,
    operacion_id: textoONulo(dos("operacion_id", "operacionId")),
  };
  // `texto` solo existe en la respuesta si quien pregunta puede verlo (superadmin con el ajuste encendido): si no viene, la clave no existe.
  if (typeof crudo.texto === "string") evento.texto = crudo.texto;
  // El detalle: solo si queda algo conocido (el servidor ya lo sanea; aquí se vuelve a filtrar para no depender de que lo haga).
  const detalle = detalleDe(crudo.detalle);
  if (detalle !== null) evento.detalle = detalle;
  return evento;
}

/**
 * Lee el listado de /bot/eventos: `{ success, data: [...], total, page, page_size }` (el backend real) o, por tolerancia, la
 * lista con otro nombre de envoltorio. Cada fila pasa por `normalizarEvento`.
 */
export function leerEventos(respuesta: unknown, filtro: BotEventosFiltro): BotEventosPagina | null {
  if (!esObjeto(respuesta)) return null;
  const lista = [respuesta.data, respuesta.eventos, respuesta.rows, respuesta.items].find(Array.isArray) as unknown[] | undefined;
  if (!lista) return null;
  const eventos = lista.filter(esObjeto).map(normalizarEvento);
  const pag = esObjeto(respuesta.pagination) ? respuesta.pagination : {};
  const total = [respuesta.total, respuesta.count, pag.total].find(esNumero);
  const pagina = [respuesta.page, pag.page].find(esNumero);
  const tamano = [respuesta.page_size, respuesta.size, respuesta.pageSize, respuesta.limit, pag.page_size, pag.size].find(esNumero);
  return {
    eventos,
    total: total ?? eventos.length,
    page: pagina ?? filtro.page ?? 1,
    pageSize: tamano ?? filtro.page_size ?? 20,
  };
}

// ─── Probar el asistente ─────────────────────────────────────────────────────

export const RESULTADOS_PRUEBA: readonly ResultadoPruebaTarea[] = [
  "ok",
  "modelo_inexistente",
  "sin_acceso",
  "clave_invalida",
  "sin_clave",
  "limite",
  "tiempo",
  "red",
  "no_sirve",
  "otro",
];

const MAX_DETALLE_PRUEBA = 200;
const MAX_MENSAJE_FALLO = 300;
/** Nombre de una variable de entorno (MAYÚSCULAS y guion bajo): lo único que el servidor puede decir de lo que falta. */
const NOMBRE_VARIABLE = /^[A-Z][A-Z0-9_]{2,40}$/;
const NOMBRE_MODELO = /^[A-Za-z0-9._:/-]{1,80}$/;

/** Un texto del servidor, sin caracteres de control, aplastado y recortado: es solo para mostrar. */
// eslint-disable-next-line no-control-regex -- justamente se buscan los caracteres de control
const textoCorto = (v: string, max: number): string => v.replace(/[\u0000-\u001F\u007F]/g, " ").replace(/\s+/g, " ").trim().slice(0, max);

function tareaDePrueba(crudo: unknown): PruebaAsistenteTarea | null {
  if (!esObjeto(crudo)) return null;
  const { resultado, modelo, ms, temperaturaEnviada, temperaturaRechazada, detalle, respaldo } = crudo;
  if (typeof resultado !== "string" || !(RESULTADOS_PRUEBA as readonly string[]).includes(resultado)) return null;
  if (typeof modelo !== "string" || !NOMBRE_MODELO.test(modelo)) return null;
  if (!esNumero(ms) || ms < 0) return null;
  if (typeof temperaturaEnviada !== "boolean" || typeof temperaturaRechazada !== "boolean" || typeof detalle !== "string") return null;
  const salida: PruebaAsistenteTarea = {
    resultado: resultado as ResultadoPruebaTarea,
    modelo,
    ms,
    temperaturaEnviada,
    temperaturaRechazada,
    detalle: textoCorto(detalle, MAX_DETALLE_PRUEBA),
  };
  if (respaldo !== undefined) {
    if (!esObjeto(respaldo) || typeof respaldo.modelo !== "string" || !NOMBRE_MODELO.test(respaldo.modelo) || typeof respaldo.ok !== "boolean") return null;
    salida.respaldo = { modelo: respaldo.modelo, ok: respaldo.ok };
  }
  return salida;
}

/**
 * Lee el `data` de POST /bot/probar-asistente. La pantalla solo muestra lo que sabe leer: cada campo se vuelve a validar aquí
 * (el servidor ya lo hace) y lo que no tiene la forma esperada invalida TODO el resultado (`null`), nunca se muestra a medias.
 */
export function leerPruebaAsistente(crudo: unknown): PruebaAsistenteResultado | null {
  if (!esObjeto(crudo) || typeof crudo.ok !== "boolean" || !esObjeto(crudo.servicio) || !esObjeto(crudo.tareas)) return null;
  const s = crudo.servicio;
  if (typeof s.openaiConfigurada !== "boolean" || !esObjeto(s.accesoAlServidor)) return null;
  const acceso = s.accesoAlServidor;
  if (typeof acceso.ok !== "boolean" || !Array.isArray(acceso.faltan)) return null;
  const faltan = [...new Set(acceso.faltan.filter((n): n is string => typeof n === "string" && NOMBRE_VARIABLE.test(n)))].slice(0, 5);

  const tareas: PruebaAsistenteResultado["tareas"] = {};
  for (const nombre of ["gasto.parse", "gasto.correct"] as const) {
    if (crudo.tareas[nombre] === undefined) continue;
    const t = tareaDePrueba(crudo.tareas[nombre]);
    if (t === null) return null;
    tareas[nombre] = t;
  }
  if (Object.keys(tareas).length === 0) return null;
  return {
    ok: crudo.ok,
    servicio: {
      openaiConfigurada: s.openaiConfigurada,
      accesoAlServidor: { ok: acceso.ok, faltan },
      version: typeof s.version === "string" && /^[0-9a-f]{7}$/.test(s.version) ? s.version : null,
      zonaHoraria: typeof s.zonaHoraria === "string" && s.zonaHoraria.length <= 40 && /^[A-Za-z_]+(?:\/[A-Za-z_+-]+){0,2}$/.test(s.zonaHoraria) ? s.zonaHoraria : null,
    },
    tareas,
  };
}

export const botAsistenteApi = {
  // ─── Modo real (ajuste botModoReal de general-settings) ────────────────────

  /**
   * ¿Está encendido el modo real? Se lee directo de general-settings (que consulta la base sin caché). `null` si no se
   * pudo leer o si el servidor no mandó un booleano: la pantalla no adivina un modo que no conoce.
   */
  leerModoReal: async (): Promise<boolean | null> => {
    const res = await apiFetch<{ data?: { botModoReal?: unknown } }>("/config-options/general-settings", { silent: true });
    const valor = res?.data?.botModoReal;
    return typeof valor === "boolean" ? valor : null;
  },

  /**
   * Enciende o apaga el modo real. Envía SOLO `botModoReal` (y la frase al encender): el PUT de general-settings es
   * todo-o-nada y mandar más claves arrastraría otros ajustes. Solo el superadministrador puede; si no, el servidor
   * responde 403 y el motivo llega como toast. Devuelve `true` si el servidor aceptó el cambio.
   */
  cambiarModoReal: async (activar: boolean, confirmacion?: string): Promise<boolean> => {
    const cuerpo: { botModoReal: boolean; confirmacion?: string } = { botModoReal: activar };
    if (activar) cuerpo.confirmacion = confirmacion;
    const res = await apiFetch("/config-options/general-settings", {
      method: "PUT",
      body: JSON.stringify(cuerpo),
      successMsg: activar ? "Modo real encendido" : "Modo simulación activado: nada de WhatsApp se guarda",
      errorMsg: "No se pudo cambiar el modo del asistente",
    });
    return res !== null;
  },

  // ─── Estado y simulaciones ──────────────────────────────────────────────────

  /** GET /bot/estado — conteos de simulaciones y de lo que el asistente guardó de verdad. */
  getEstado: async (): Promise<BotEstado | null> => {
    const res = await apiFetch<BotEstadoResponse>("/bot/estado", { silent: true });
    return res?.data ?? null;
  },

  /** GET /bot/simulaciones — lo que el asistente habría guardado (más nuevo primero). */
  listarSimulaciones: async (filtro: BotSimulacionesFiltro = {}): Promise<BotSimulacionesPagina | null> => {
    const res = await apiFetch<Partial<BotSimulacionesPagina>>(
      `/bot/simulaciones${consulta({ page: filtro.page, size: filtro.size, estado: filtro.estado })}`,
      { silent: true },
    );
    if (!res || !Array.isArray(res.data)) return null;
    return {
      data: res.data as BotSimulacion[],
      total: esNumero(res.total) ? res.total : res.data.length,
      page: esNumero(res.page) ? res.page : (filtro.page ?? 1),
      size: esNumero(res.size) ? res.size : (filtro.size ?? 20),
    };
  },

  /**
   * DELETE /bot/simulaciones — borra TODAS las simulaciones (y solo ellas: ningún gasto real). El servidor exige la
   * frase; la pantalla se la pide a la persona antes de llamar aquí.
   */
  vaciarSimulaciones: (confirmacion: string): Promise<BotVaciarSimulacionesResultado | null> =>
    apiFetch<BotVaciarSimulacionesResultado>("/bot/simulaciones", {
      method: "DELETE",
      body: JSON.stringify({ confirmacion }),
      errorMsg: "No se pudieron vaciar las simulaciones",
    }),

  // ─── Actividad reciente ─────────────────────────────────────────────────────

  /** GET /bot/eventos — bitácora de lo que pasó con cada mensaje (más nuevo primero). */
  listarEventos: async (filtro: BotEventosFiltro = {}): Promise<BotEventosPagina | null> => {
    const res = await apiFetch<unknown>(
      `/bot/eventos${consulta({
        page: filtro.page,
        page_size: filtro.page_size,
        desde: filtro.desde,
        hasta: filtro.hasta,
        resultado: filtro.resultado,
        origen: filtro.origen,
        simulado: filtro.simulado,
        menu: filtro.menu,
      })}`,
      { silent: true },
    );
    return leerEventos(res, filtro);
  },

  // ─── Probar el asistente ─────────────────────────────────────────────────────

  /**
   * POST /bot/probar-asistente — prueba, sin guardar nada, el modelo y la temperatura que están ESCRITOS en el formulario. Nunca lanza ni
   * avisa por su cuenta: devuelve el resultado, o POR QUÉ no se pudo (estado, código y mensaje del servidor) para que la pantalla lo
   * explique en el lugar. Manda SOLO `tareas` (el servidor rechaza cualquier otro campo). Un 401 cierra la sesión como en todo el sitio.
   */
  probarAsistente: async (tareas: PruebaAsistenteTareas): Promise<LlamadaPruebaAsistente> => {
    const registro: { fallo: PruebaAsistenteFallo | null } = { fallo: null };
    const res = await apiFetch<unknown>("/bot/probar-asistente", {
      method: "POST",
      body: JSON.stringify({ tareas }),
      silent: true,
      alFallar: (e) => {
        registro.fallo = { status: e.status, code: e.code, message: textoCorto(String(e.message ?? ""), MAX_MENSAJE_FALLO) };
      },
    });
    if (res === null) return { tipo: "fallo", fallo: registro.fallo ?? { status: null, code: null, message: "" } };
    const datos = leerPruebaAsistente(esObjeto(res) ? res.data : null);
    if (datos === null) return { tipo: "fallo", fallo: { status: 200, code: "RESPUESTA_INVALIDA", message: "" } };
    return { tipo: "resultado", datos };
  },

  // ─── Ajustes del asistente (twilio-settings) ────────────────────────────────

  /** GET /config-options/twilio-settings — ajustes vigentes, valores de fábrica y estado de los modos de prueba. */
  getTwilioSettings: async (): Promise<{ actual: TwilioSettingsLectura; defaults: TwilioAjustes } | null> => {
    const res = await apiFetch<unknown>("/config-options/twilio-settings", { silent: true });
    return leerTwilioSettings(res);
  },

  /**
   * PUT /config-options/twilio-settings — manda SOLO las claves que cambian. `simuladorActivo` / `diagnosticoActivo` son
   * del superadministrador (403 SOLO_SUPERADMIN para otro rol). Devuelve `true` si el servidor aceptó.
   */
  updateTwilioSettings: async (cambio: TwilioSettingsCambio, mensajes: { exito?: string; error?: string } = {}): Promise<boolean> => {
    const res = await apiFetch("/config-options/twilio-settings", {
      method: "PUT",
      body: JSON.stringify(cambio),
      successMsg: mensajes.exito ?? "Ajustes del asistente guardados",
      errorMsg: mensajes.error ?? "No se pudieron guardar los ajustes del asistente",
    });
    return res !== null;
  },
};
